import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { AuthKitProvider, useAuth } from "@workos-inc/authkit-react";
import { ConvexReactClient } from "convex/react";
import { ConvexProviderWithAuthKit } from "@convex-dev/workos";

import "@fontsource-variable/geist";
import "@fontsource-variable/geist-mono";
import "@/styles/globals.css";

import { ThemeProvider } from "@/components/theme-provider";
import { ReactScan } from "@/components/react-scan";
import { PWAUpdatePrompt } from "@/components/pwa-update-prompt";
import { InstallAppPrompt } from "@/components/install-app-prompt";
import { getWorkOSAuthKitConfig } from "@/lib/workos-auth-config";
import {
    suggestedCloudUrlFromSite,
    validateConvexDeploymentUrl,
} from "@/lib/convex-url";
import App from "@/frontend/app";

const rootElOrNull = document.getElementById("root");
if (!rootElOrNull) {
    throw new Error("Missing #root element");
}
const rootEl = rootElOrNull;

const rawConvexUrl = import.meta.env.NEXT_PUBLIC_CONVEX_URL;
const convexUrlCheck = validateConvexDeploymentUrl(rawConvexUrl);

function renderBootError(title: string, bodyHtml: string) {
    rootEl.innerHTML = `
      <main style="min-height:100vh;display:grid;place-items:center;padding:2rem;font-family:system-ui,sans-serif;background:#0a0e14;color:#e2e8f0">
        <div style="max-width:36rem">
          <h1 style="font-size:1.5rem;margin:0 0 .5rem">${title}</h1>
          <div style="margin:0;line-height:1.5;color:#94a3b8">${bodyHtml}</div>
        </div>
      </main>
    `;
}

if (!convexUrlCheck.ok) {
    // Bad/missing build-time env used to throw and leave a blank dark page.
    if (convexUrlCheck.reason === "missing") {
        renderBootError(
            "Aurora Forecaster failed to start",
            `<p style="margin:0 0 1rem">
              <code style="color:#e2e8f0">NEXT_PUBLIC_CONVEX_URL</code> is not set for this deployment.
            </p>
            <p style="margin:0">
              Add it in the Vercel project environment variables (Preview + Production), then
              <strong style="color:#e2e8f0">redeploy</strong> — Vite inlines this value at build time.
            </p>`,
        );
    } else if (convexUrlCheck.reason === "http_site") {
        const suggested =
            suggestedCloudUrlFromSite(rawConvexUrl ?? "") ??
            "https://&lt;deployment&gt;.convex.cloud";
        renderBootError(
            "Wrong Convex URL host",
            `<p style="margin:0 0 1rem">
              <code style="color:#e2e8f0">NEXT_PUBLIC_CONVEX_URL</code> is set to a
              <code style="color:#e2e8f0">.convex.site</code> address. That host is only for HTTP
              actions and webhooks.
            </p>
            <p style="margin:0 0 1rem">
              The client needs the deployment URL ending in
              <code style="color:#e2e8f0">.convex.cloud</code>, for example:
            </p>
            <p style="margin:0 0 1rem">
              <code style="color:#e2e8f0;word-break:break-all">${suggested}</code>
            </p>
            <p style="margin:0">
              Update the Vercel env var, then <strong style="color:#e2e8f0">redeploy</strong> this preview.
            </p>`,
        );
    } else {
        renderBootError(
            "Invalid Convex URL",
            `<p style="margin:0">
              <code style="color:#e2e8f0">NEXT_PUBLIC_CONVEX_URL</code> must be a full URL such as
              <code style="color:#e2e8f0">https://&lt;deployment&gt;.convex.cloud</code>.
              Fix it in Vercel, then redeploy.
            </p>`,
        );
    }
} else {
    const convex = new ConvexReactClient(convexUrlCheck.url);
    const { apiHostname, devMode } = getWorkOSAuthKitConfig();

    function Root() {
        return (
            <StrictMode>
                <ReactScan />
                <ThemeProvider
                    attribute="class"
                    defaultTheme="system"
                    enableSystem
                    disableTransitionOnChange
                >
                    <AuthKitProvider
                        clientId={import.meta.env.NEXT_PUBLIC_WORKOS_CLIENT_ID ?? ""}
                        apiHostname={apiHostname}
                        devMode={devMode}
                        redirectUri={window.location.origin}
                    >
                        <ConvexProviderWithAuthKit client={convex} useAuth={useAuth}>
                            <App />
                            <PWAUpdatePrompt />
                            <InstallAppPrompt />
                        </ConvexProviderWithAuthKit>
                    </AuthKitProvider>
                </ThemeProvider>
            </StrictMode>
        );
    }

    createRoot(rootEl).render(<Root />);
}
